import {
    AssetRegulation,
    CampaignStatus,
    CampaignUseOfFundsBadge,
} from '@src/enums'
import {
    Asset,
    ContentDocument,
    ImageInterface,
    PaymentMethods,
} from '@src/types'
import type { ImageField } from '@builder/types'

export class Campaign {
    asset_var_uid: string | null = null
    asset_uid: string | null = null
    platform_name: string | null = null
    language: string | null = null
    number_locale: string | null = null
    country: string | null = null
    funding_start: string | number | Date | null = null
    funding_end: string | number | Date | null = null
    waitlist_url: string | null = null
    waitlist_text: string | null = null
    asset_title: string | null = null
    asset_asset_title: string | null = null
    asset_subtitle: string | null = null
    asset_summary: string | null = null
    asset_excerpt: string | null = null
    asset_description: string | null = null
    asset_needs_contract_selection = false
    investment_summary: string | null = null
    thumb_image: ImageInterface | null = null
    hero_image: ImageInterface | null = null
    content_images: ImageInterface[] = []
    content_documents: ContentDocument[] = []
    asset_video: string | null = null
    status: CampaignStatus = CampaignStatus.DRAFT
    broker: Broker | null = null
    funding_stats_visible = false
    regulatory_requirements: number | null = 0
    is_co_placement = false
    asset_regulation_override: AssetRegulation | null = null
    badge: number | null = null
    asset_var_password: string | null = null
    agio_disagio = 0
    agio_disagio_rate: number | null = null
    agio_disagio_type = 0
    agio_disagio_title: string | null = null
    agio_disagio_disclaimer: string | null = null
    agio_disagio_info_text: string | null = null
    savings_plan = false
    savings_plan_description: string | null = null
    payment_methods: PaymentMethods = new PaymentMethods()
    voucher_code: string | null = null
    voucher_rate: number | null = null
    additional_fields: CampaignAdditionalFields | null = null
    additional_fields_enabled = false
    external_project_url: string | null = null
    show_amount_slider = false
    assets_incentives: AssetsIncentive[] = []
    assets_meta: AssetsMeta[] = []
    team_members: TeamMember[] = []
    created_on: string | number | Date | null = null
    updated_at: string | number | Date | null = null
    asset: Asset | null = null
    display_price_without_agio = false
    campaign_additional_info: string | null = null
    agio_disagio_feature_active = false
    disable_invest_button = false
    sm_pre_emptive_rights_required = false
    asset_information: CampaignMarketingKeyValue[] = []
    company_overview: string | null = null
    company_key_facts: CampaignMarketingKeyValue[] = []
    company_key_facts_note: string | null = null
    risk_disclaimer: string | null = null
    company_milestones: CampaignMarketingKeyValue[] = []
    company_team_overview: string | null = null
    white_label_box_title: string | null = null
    white_label_box_icon: string | null = null
    white_label_box_description: string | null = null
    podcast_id: string | null = null
    news: string[] = []
    community_link: string | null = null
    business_model: string | null = null
    offerings: CampaignMarketingTitleDescription[] = []
    revenue_structure: CampaignMarketingRevenueStructure[] = []
    competitive_advantages: CampaignMarketingTitleDescription[] = []
    market_segment: string | null = null
    trends: CampaignMarketingTitleDescription[] = []
    competition: string | null = null
    swot_analysis: CampaignMarketingSwotItem[] = []
    past_financial_data_introduction: string | null = null
    past_financial_tables: FinancialTable[] = []
    current_status: string | null = null
    current_status_boxes: CampaignCurrentStatusBox[] | null = null
    projected_financial_data_introduction: string | null = null
    projected_financial_tables: FinancialTable[] = []
    use_of_funds_description: string | null = null
    use_of_funds_total: number | null = null
    use_of_funds_threshold: number | null = null
    use_of_funds_fully_funded: number | null = null
    use_of_funds_items: CampaignUseOfFundsItem[] = []
    esg_score: number | null = null
    esg_description: string | null = null
    esg_cards: CampaignMarketingTitleDescription[] = []
    esg_sdgs: CampaignEsgSdg[] = []
    esg_document: ImageInterface | null = null
    slug: string | null = null
    og_title: string | null = null
    og_description: string | null = null
    og_image: ImageField | null = null
    focus_keyword: string | null = null
}

export class CampaignMarketingKeyValue {
    label = ''
    value = ''
}

export class CampaignEsgSdg {
    sdg_number: number | null = null
    description = ''
}

export class CampaignMarketingTitleDescription {
    title = ''
    description = ''
}

export class CampaignMarketingRevenueStructure {
    title = ''
    description = ''
    percentage: number | null = null
}

export class CampaignMarketingSwotItem {
    title = ''
    description = ''
    type: 'success' | 'error' | null = null
}

export class FinancialRow {
    id = ''
    label = ''
    values: Record<string, string | null> = {}
}

export class FinancialTable {
    id = ''
    title = ''
    periods: string[] = []
    rows: FinancialRow[] = []
    note: string | null = null
}

export class CampaignCurrentStatusBox {
    label = ''
    value = ''
    sublabel = ''
}

export class CampaignUseOfFundsItem {
    id = ''
    label = ''
    minimum_percentage: number | null = null
    full_percentage: number | null = null
    minimum_badge: CampaignUseOfFundsBadge | null = null
    full_badge: CampaignUseOfFundsBadge | null = null
}

export class DuplicatedCampaign {
    asset_title: string | null = null
    platform_name: string | null = null
}

export class Broker {
    broker_status = false
    broker_disclaimer: string | null = null
    broker_field_required = false
}

export class AssetsIncentive {
    asset_inc_uid?: string | null = null
    incentives_amount: number | null = null
    incentives_limit = 0
    incentives_title: string | null = null
    incentives_description: string | null = null
    position = 0
    deleted = false
    migrated = false
}

export class AssetsMeta {
    asset_meta_uid?: string | null = null
    meta_title: string | null = null
    meta_value: string | null = null
    placing = 0
}

export class TeamMember {
    id?: string | null = null
    name: string | null = null
    title: string | null = null
    picture: ImageInterface | null = null
    bio: string | null = null
    position = 0
}

export class CampaignAdditionalFields {
    depot_owner: { required: boolean } = { required: false }
    depot_number: { required: boolean } = { required: false }
    depot_bank: { required: boolean } = { required: false }
    depot_bic: { required: boolean } = { required: false }
}

// A campaign linked (or linkable) as a language version, see CampaignTranslations.vue.
export interface CampaignTranslation {
    asset_var_uid: string
    slug: string | null
    language: string
    asset_title: string
    status: number
    // "<campaign title> (<campaign status>)"
    label: string
    is_x_default: boolean
}

export interface CampaignTranslationsResponse {
    is_x_default: boolean
    linked_translations: CampaignTranslation[]
    candidates: CampaignTranslation[]
}
